import { EditIcon, RotateIcon } from '@persona-id/design-tokens/icons';
import { isEmpty } from 'lodash/fp';
import { useEffect, VFC } from 'react';
import styled from 'styled-components';

import FieldSchemaSelect from 'dashboard/components/FieldSchemaSelect';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import { StepAdvancedConfigurationWithErrorHandlingPanel } from 'dashboard/components/WorkflowControlPanel/StepAdvancedConfigurationWithErrorHandlingPanel';
import FieldMappingsPanel from 'dashboard/components/WorkflowControlPanel/StepConfigAction/FieldMappingsPanel';
import FieldsList from 'dashboard/components/WorkflowControlPanel/StepConfigAction/FieldsList';
import { ParamsBaseProps } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ParamsBaseProps';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import { getTokensToDisplayValue, snakify, UsableObjectConfig } from 'dashboard/lib/workflows';
import { REPORT_TEMPLATE_TYPE_TO_FIELDS, ReportTemplate } from 'dashboard/models/report-template';
import { Trigger } from 'dashboard/models/trigger';
import { WorkflowError } from 'dashboard/models/workflow';
import { WorkflowActionRunReportWithParamsV1 } from 'dashboard/models/workflow-version';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import useToggle from 'lib/hooks/useToggle';

const InputFieldsHeading = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

interface ParamsRunReportWithParamsV1Props extends ParamsBaseProps {
  reportTemplates: {
    name: string;
    token: string;
    type: ReportTemplate['type'];
    isArchived: boolean;
  }[];
  editable: boolean;
  errors: WorkflowError[];
  params: WorkflowActionRunReportWithParamsV1['params'];
  trigger?: Trigger;
  usableObjects: UsableObjectConfig[];
  updateActionParams: (newParams: WorkflowActionRunReportWithParamsV1['params']) => void;
}

const ParamsRunReportWithParamsV1: VFC<ParamsRunReportWithParamsV1Props> = ({
  step,
  reportTemplates,
  trigger,
  usableObjects,
  editable,
  errors,
  params,
  updateActionParams,
  updateStep,
}) => {
  const [showEditPanel, { toggle: toggleEditPanel }] = useToggle(false);

  const defaultFieldMapping: Record<string, string> = reportTemplates.reduce<
    Record<string, string>
  >((acc, template) => {
    const fields = REPORT_TEMPLATE_TYPE_TO_FIELDS[template.type];
    const fieldMapping = (fields || []).reduce((mapping: Record<string, string>, field: string) => {
      return {
        ...mapping,
        [field]: `trigger_params.${snakify(field)}`,
      };
    }, {});
    acc = { ...acc, ...fieldMapping };
    return acc;
  }, {});

  const updateRunReportParams = <Key extends keyof WorkflowActionRunReportWithParamsV1['params']>(
    key: Key,
    value: WorkflowActionRunReportWithParamsV1['params'][Key],
  ) => {
    updateActionParams({
      ...params,
      [key]: value,
    });
  };

  useEffect(() => {
    if (isEmpty(params['report-fields'])) {
      updateRunReportParams('report-fields', defaultFieldMapping);
    }
  }, []);

  const { hasErrorFor, renderErrorMessageFor } =
    useWorkflowError<keyof WorkflowActionRunReportWithParamsV1['params']>(errors);

  return (
    <>
      <Spacer height={2} />
      <ParamLabel
        labelText='Report Template'
        htmlFor='params-run-report-with-params__report-template-input'
      />
      <FieldSchemaSelect
        size='md'
        id='params-run-report-with-params__report-template'
        initialValue={params['report-template-id']}
        usableObjects={usableObjects}
        onFieldSelect={(option) => updateRunReportParams('report-template-id', option.value)}
        disabled={!editable}
        hasError={hasErrorFor('report-template-id')}
      />
      {renderErrorMessageFor('report-template-id')}
      {showEditPanel && (
        <>
          <Spacer height={2} />
          <FieldMappingsPanel
            title='Report fields'
            usableObjects={usableObjects}
            fieldMappings={params['report-fields'] ?? {}}
            onOutsideClick={toggleEditPanel}
            onUpdateFieldMappings={(values: Record<string, string>) =>
              updateRunReportParams('report-fields', values)
            }
          />
        </>
      )}
      <Spacer height={4} />
      <StepAdvancedConfigurationWithErrorHandlingPanel
        step={step}
        trigger={trigger}
        updateStep={updateStep}
        disabled={!editable}
      >
        <Spacer height={2} />
        {editable && (
          <InputFieldsHeading>
            <ParamLabel
              labelText='Report Fields hello'
              htmlFor='params-run-report-with-params__report-fields'
            />
            <HStack>
              <Button
                variant='basic'
                onClick={toggleEditPanel}
                aria-label='Open edit panel'
                id='params-run-report-with-params__report-fields'
              >
                <EditIcon />
              </Button>
              <Button
                variant='basic'
                onClick={() => updateRunReportParams('report-fields', defaultFieldMapping)}
                aria-label='Restore default fields'
                id='params-run-report-with-params__report-fields-restore'
              >
                <RotateIcon />
              </Button>
            </HStack>
          </InputFieldsHeading>
        )}
        {renderErrorMessageFor('report-fields')}
        <Spacer height={2} />
        <FieldsList
          editable={editable}
          toggleEditPanel={toggleEditPanel}
          tokensToDisplayValue={getTokensToDisplayValue(usableObjects)}
          fields={params['report-fields']}
          fieldKey='report-fields'
          errors={errors}
        />
        <Spacer height={2} />
      </StepAdvancedConfigurationWithErrorHandlingPanel>
    </>
  );
};

export default ParamsRunReportWithParamsV1;
